import { useState } from "react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { API, http, authHeaders, formatApiError, CAREERS_TOKEN } from "../../../lib/api";

const TOOLBAR = [[{ header: [2, 3, false] }], ["bold", "italic", "underline"], [{ list: "ordered" }, { list: "bullet" }], ["link"], ["clean"]];
const today = () => new Date().toISOString().slice(0, 10);
const blank = () => ({ job_code: "", title: "", organisation: "", posted_on: today(), apply_by: "", jd_html: "" });

const Field = ({ label, children, className = "" }) => (
  <label className={`block ${className}`}>
    <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">{label}</span>
    {children}
  </label>
);

export const JobEditor = ({ job, open, onClose, onSaved }) => {
  const [f, setF] = useState(() => (job ? { ...job } : blank()));
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    if (!f.jd_html || f.jd_html.replace(/<[^>]+>/g, "").trim().length === 0) return toast.error("Please add the job description.");
    setSaving(true);
    const body = { job_code: f.job_code, title: f.title, organisation: f.organisation, posted_on: f.posted_on, apply_by: f.apply_by, jd_html: f.jd_html };
    try {
      const req = job?.id ? http.put(`${API}/careers/admin/jobs/${job.id}`, body, { headers: authHeaders(CAREERS_TOKEN) }) : http.post(`${API}/careers/admin/jobs`, body, { headers: authHeaders(CAREERS_TOKEN) });
      const { data } = await req;
      toast.success(job?.id ? "Job updated" : "Job published");
      onSaved(data);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto bg-[#111519] border-white/10 text-zinc-100" data-testid="job-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-medium">{job?.id ? "Edit job" : "New job"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 gap-6 sm:grid-cols-2" data-testid="job-editor-form">
          <Field label="Job ID *"><input required className="field uppercase" value={f.job_code} onChange={set("job_code")} placeholder="e.g. PLK-CXO-101" data-testid="job-editor-code" /></Field>
          <Field label="Job title *"><input required className="field" value={f.title} onChange={set("title")} placeholder="e.g. Chief Risk Officer" data-testid="job-editor-title" /></Field>
          <Field label="Organisation name *" className="sm:col-span-2"><input required className="field" value={f.organisation} onChange={set("organisation")} placeholder="Client name or 'Leading Private Bank'" data-testid="job-editor-org" /></Field>
          <Field label="Date of posting *"><input required type="date" className="field [color-scheme:dark]" value={f.posted_on} onChange={set("posted_on")} data-testid="job-editor-posted" /></Field>
          <Field label="Apply by date *"><input required type="date" min={f.posted_on} className="field [color-scheme:dark]" value={f.apply_by} onChange={set("apply_by")} data-testid="job-editor-apply-by" /></Field>
          <div className="sm:col-span-2" data-testid="job-editor-jd">
            <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-zinc-400">Full job description *</span>
            <div className="quill-dark mt-3">
              <ReactQuill theme="snow" value={f.jd_html} onChange={(v) => setF((p) => ({ ...p, jd_html: v }))} modules={{ toolbar: TOOLBAR }} />
            </div>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3">
            <button type="button" onClick={onClose} className="rounded-full border border-white/15 px-5 py-2.5 text-sm" data-testid="job-editor-cancel">Cancel</button>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-6 py-2.5 text-sm font-medium text-[#0a0c0e] hover:bg-emerald-400 disabled:opacity-60" data-testid="job-editor-save">
              {saving && <Loader2 className="h-4 w-4 animate-spin" />} {job?.id ? "Save changes" : "Publish job"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
